/**
 * Turning a piece of status into somewhere you can actually go.
 *
 * Two problems this solves:
 *
 *  1. The server reports service URLs exactly as configured, and most of them
 *     are loopback (`http://127.0.0.1:8989`). That is correct for the server —
 *     it is the one making the calls — but a phone opening the console at
 *     `http://10.1.0.10:8789` cannot follow a 127.0.0.1 link. So loopback is
 *     rewritten to whatever host the console itself was reached on.
 *
 *  2. Some things on screen have no other UI to open — a volume, a fixer log
 *     line. Those get an "ask about this" link into chat instead, which is
 *     still taking you to the data, just by the route this app actually has.
 */

const LOOPBACK = /^(127\.0\.0\.1|localhost|\[::1\]|0\.0\.0\.0)$/i;

/** Rewrite a server-side URL so it resolves from wherever the client is. */
export function externalUrl(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  try {
    const u = new URL(url);
    if (LOOPBACK.test(u.hostname) && typeof window !== "undefined") {
      const here = window.location.hostname;
      // Only rewrite when we are *not* also on loopback, or we would rewrite
      // a working link into the same working link for no reason.
      if (here && !LOOPBACK.test(here)) u.hostname = here;
    }
    return u.toString();
  } catch {
    return undefined;
  }
}

/** Deep link into chat with the question already typed. */
export function askUrl(question: string): string {
  return `/chat?ask=${encodeURIComponent(question)}`;
}

/**
 * Plex's web client lives at /web, not at the API root, and Sonarr/Radarr
 * queue and system pages are worth linking to directly.
 */
export function serviceUrl(url: string | undefined, path = ""): string | undefined {
  const base = externalUrl(url);
  if (!base) return undefined;
  return base.replace(/\/+$/, "") + path;
}
